import React, { useEffect } from "react";
import { StyledFormElementsWrapper, ContainerGrid, FormLabel } from "../styles";
import FormField from "../../../common/FormField/FormField";
import { CustomFileUpload, CustomInputField } from "../../../common/FormInputs";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import AWSService from "../../../../services/AWSService";
import NoticeService from "../../../../services/NoticeService";
import {
  Grid,
  Radio,
  RadioGroup,
  makeStyles,
  FormControlLabel,
} from "@material-ui/core";
import styled from "styled-components";
import { SecondaryCTAButton } from "../../../common/Buttons";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";

const validFormat = /(?:image)/;

const useStyles = makeStyles(() => ({
  formLabel: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#293461",
  },
}));

const HeaderFooter = ({
  values,
  handleChange,
  errors,
  touched,
  handleBlur,
  setFieldValue,
  setFind,
  letterMenuItems,
  setLetterMenuItems,
  triggerNotify,
  setTriggerNotify,
  radioInputs,
}) => {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(file, field, setFieldValue) {
    if (file) {
      try {
        const errorMessage = "Invalid File Type";
        if (!validFormat.test(file?.type)) throw errorMessage;
        setLoader({ state: true, message: `Uploading file ${file.name} ...` });
        const payload = { key: file.name };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
          enqueueSnackbar("Upload was successful", { variant: "success" });
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  useEffect(() => {
    if (values?.letterHeadName) {
      let result = letterMenuItems?.filter(
        (item) => item?.value === values?.letterHeadName,
      )[0];
      if (result) {
        let metaData = JSON.parse(result?.headerFooterText);
        setFieldValue("headerImage", result?.headerImage);
        setFieldValue("footerImage", result?.footerImage);
        if (
          !JSON.parse(result?.headerFooterText) ||
          (!metaData.headerText?.length && !metaData.footerText?.length)
        ) {
          setFieldValue("headerText", [""]);
          setFieldValue("footerText", [""]);
        } else {
          setFieldValue("headerText", metaData?.headerText || [""]);
          setFieldValue("footerText", metaData?.footerText || [""]);
        }
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.letterHeadName]);

  useEffect(() => {
    async function getLetterHeadOption() {
      try {
        setLoader({ state: true, message: "fetching details..." });
        const response = await NoticeService.getLetterHeadOption();
        if (response) {
          setLetterMenuItems(
            response.map(
              ({
                letterHeadName,
                id,
                headerImage,
                footerImage,
                headerFooterText,
              }) => ({
                id,
                label: letterHeadName,
                value: letterHeadName,
                headerImage,
                footerImage,
                headerFooterText,
              }),
            ),
          );
        }
      } catch (err) {
        enqueueSnackbar(err?.message, { variant: "error" });
      } finally {
        setLoader({ state: false });
        setTriggerNotify(false);
      }
    }
    if (triggerNotify) getLetterHeadOption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNotify]);

  const inputs = [
    {
      type: "autoComplete",
      name: "letterHeadName",
      label: "Letter Head Name",
      required: true,
    },
  ];

  const letter_head_images = [
    {
      name: "headerImage",
      label: "Header Image",
      type: "upload",
      accept: "image/*",
    },
    {
      name: "footerImage",
      label: "Footer Image",
      type: "upload",
      accept: "image/*",
    },
  ];

  const addMoreText = (fieldArray, setFieldValue, fieldName) => {
    setFieldValue(fieldName, [...fieldArray, ""]);
  };

  const handleDeleteText = (index, fieldArray, setFieldValue, fieldName) => {
    if (fieldArray?.length) {
      const filtered = fieldArray.filter((_, idx) => idx !== index);
      setFieldValue(fieldName, filtered);
    }
  };

  return (
    <StyledFormElementsWrapper style={{ marginBottom: 8, marginTop: 15 }}>
      <SectionTitle>Add Letter Head</SectionTitle>

      {/* Radio: Image / Text */}
      <RadioSectionLabel>Letter Head Type</RadioSectionLabel>
      <Grid item xs={12} style={{ marginBottom: "20px" }}>
        <RadioGroup
          style={{ flexDirection: "row", gap: "8px", marginLeft: "4px" }}
          name="typeOfLetterHead"
          value={values.typeOfLetterHead}
          onChange={(e) => setFieldValue("typeOfLetterHead", e.target.value)}
        >
          {radioInputs.map((radio, key) => (
            <RadioChip
              key={key}
              selected={values.typeOfLetterHead === radio.value}
            >
              <FormControlLabel
                classes={{ label: classes.formLabel }}
                value={radio.value}
                control={
                  <Radio
                    size="small"
                    style={{ color: COLORS.BTN_GREEN, padding: "6px" }}
                  />
                }
                label={radio.label}
                style={{ margin: 0 }}
              />
            </RadioChip>
          ))}
        </RadioGroup>
      </Grid>

      <ContainerGrid>
        {inputs.map((input, index) => (
          <FormField
            menuItems={letterMenuItems}
            key={index}
            {...{
              touched,
              errors,
              input,
              values,
              handleChange,
              handleBlur,
              setFieldValue,
              setFind,
            }}
          />
        ))}

        {values?.typeOfLetterHead === "image"
          ? letter_head_images.map((image, index) => (
              <UploadBlock key={index}>
                <FormLabel>{image.label}</FormLabel>
                <CustomFileUpload
                  error={touched[image.name] && errors[image.name]}
                  setFile={(file) => s3Upload(file, image.name, setFieldValue)}
                  style={{ width: "100%" }}
                  accept={image.accept}
                  file={{ name: getFileName(values[image.name]) }}
                />
              </UploadBlock>
            ))
          : null}
      </ContainerGrid>

      {values?.typeOfLetterHead === "text" ? (
        <ContainerGrid style={{ marginTop: 12 }}>
          {/* Header Text */}
          <TextSection>
            <TextSectionLabel>Header Text</TextSectionLabel>
            {values.headerText.map((itemText, index) => (
              <TextRow key={index}>
                <CustomInputField
                  variant="outlined"
                  style={{ width: "100%", marginTop: 6 }}
                  name={`headerText[${index}]`}
                  value={itemText}
                  onChange={handleChange}
                />
                {values.headerText.length > 1 && (
                  <DeleteBtn
                    onClick={() =>
                      handleDeleteText(
                        index,
                        values.headerText,
                        setFieldValue,
                        "headerText",
                      )
                    }
                  >
                    <img
                      src={require("../../../../assets/images/removeIcon.svg")}
                      alt="delete"
                    />
                  </DeleteBtn>
                )}
              </TextRow>
            ))}
            <SecondaryCTAButton
              style={{ marginTop: "20px" }}
              onClick={() =>
                addMoreText(values.headerText, setFieldValue, "headerText")
              }
            >
              + Add next line
            </SecondaryCTAButton>
          </TextSection>

          {/* Footer Text */}
          <TextSection>
            <TextSectionLabel>Footer Text</TextSectionLabel>
            {values.footerText.map((itemText, index) => (
              <TextRow key={index}>
                <CustomInputField
                  variant="outlined"
                  style={{ width: "100%", marginTop: 6 }}
                  name={`footerText[${index}]`}
                  value={itemText}
                  onChange={handleChange}
                />
                {values.footerText.length > 1 && (
                  <DeleteBtn
                    onClick={() =>
                      handleDeleteText(
                        index,
                        values.footerText,
                        setFieldValue,
                        "footerText",
                      )
                    }
                  >
                    <img
                      src={require("../../../../assets/images/removeIcon.svg")}
                      alt="delete"
                    />
                  </DeleteBtn>
                )}
              </TextRow>
            ))}
            <SecondaryCTAButton
              style={{ marginTop: "20px" }}
              onClick={() =>
                addMoreText(values.footerText, setFieldValue, "footerText")
              }
            >
              + Add next line
            </SecondaryCTAButton>
          </TextSection>
        </ContainerGrid>
      ) : null}
    </StyledFormElementsWrapper>
  );
};

export default HeaderFooter;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1.5px solid rgba(0, 131, 140, 0.1);
`;

const RadioSectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 700;
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  margin-left: 4px;
`;

const RadioChip = styled.div`
  padding: 6px 14px;
  border-radius: 12px;
  border: 1.5px solid
    ${({ selected }) => (selected ? COLORS.BTN_GREEN : "#e1e3ee")};
  background: ${({ selected }) =>
    selected ? "rgba(0, 131, 140, 0.07)" : "#f9fbfc"};
  transition: all 0.2s ease;
  cursor: pointer;
`;

const UploadBlock = styled.div`
  margin-top: 10px;
`;

const TextSection = styled.div`
  margin-top: 10px;
`;

const TextSectionLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  font-weight: 700;
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const TextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DeleteBtn = styled.div`
  margin-top: 8px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s;
  &:hover {
    opacity: 1;
  }
`;
